import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { styled } from "styled-components";

import { getAdminNotice, updateAdminNotice } from "shared/api/AdminNoticeApi";
import { ModalButton } from "shared/ui/buttons";
import { confirmDialog, ModalShell } from "shared/ui/feedback";
import { ButtonBar } from "shared/ui/layout";

const MAX_LENGTH = 40;

/**
 * 관리자 페이지 공지(전역 헤더 좌측 노출) 등록/수정 모달.
 * 열릴 때 현재 공지를 불러와 채우고, 저장 시 전역 헤더에 반영되도록 새로고침한다.
 */
export const AdminNoticeModal = ({ isModalOpen, setIsModalOpen }) => {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [tempNotice, setTempNotice] = useState("");

  useEffect(() => {
    if (!isModalOpen) return;
    getAdminNotice()
      .then((data) => {
        setNotice(data?.content ?? "");
        setTempNotice(data?.content ?? "");
      })
      .catch(() => {});
  }, [isModalOpen]);

  const onChangeNotice = (event) => {
    if (event.target.value.length <= MAX_LENGTH) {
      setTempNotice(event.target.value);
    }
  };

  const handleCancel = async () => {
    // 변경 사항이 없으면 확인창 없이 바로 닫는다.
    if (tempNotice === (notice ?? "")) {
      setIsModalOpen(false);
      return;
    }
    const shouldProceed = await confirmDialog(
      "취소 시 작성 중인 내용이 사라집니다.\n그래도 취소하시겠습니까?",
    );
    if (shouldProceed) setIsModalOpen(false);
  };

  const handleSave = async () => {
    const shouldProceed = await confirmDialog(
      "공지를 저장하시겠습니까?\n해당 공지는 모든 관리자 페이지 상단에 노출됩니다.",
    );
    if (!shouldProceed) return;
    try {
      await updateAdminNotice(tempNotice);
      // 전역 헤더(useQuery: ["adminNotice"])가 쓰는 캐시를 직접 갱신해
      // 새로고침 없이 헤더만 즉시 리렌더되게 한다.
      queryClient.setQueryData(["adminNotice"], { content: tempNotice });
      setNotice(tempNotice);
      setIsModalOpen(false);
    } catch (error) {
      // 실패 시 모달 유지
    }
  };

  const disabled = tempNotice.length === 0 || tempNotice === (notice ?? "");

  return (
    <ModalShell isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)}>
      <h2 style={{ fontWeight: "bold" }}>관리자 페이지 공지 관리</h2>
      <Column style={{ flexGrow: "1" }}>
        <Row>
          <span style={{ flexBasis: "100px" }}>공지 내용</span>
          <TextInput
            placeholder="40자 이내로 작성해주세요."
            value={tempNotice}
            onChange={onChangeNotice}
            type="text"
          />
        </Row>
        <Row style={{ justifyContent: "flex-end" }}>
          <span
            style={{
              fontSize: "15px",
              fontWeight: "400",
              opacity: "0.3",
              marginTop: "2px",
            }}
          >
            {tempNotice.length}/{MAX_LENGTH}
          </span>
        </Row>
      </Column>
      <ButtonBar style={{ gap: "30px" }}>
        <ModalButton onClick={handleCancel}>취소</ModalButton>
        <ModalButton
          style={
            disabled
              ? {
                  cursor: "not-allowed",
                  backgroundColor: "#e8e8e8",
                  color: "white",
                }
              : { color: "white", backgroundColor: "#0079E0" }
          }
          onClick={disabled ? () => {} : handleSave}
        >
          저장
        </ModalButton>
      </ButtonBar>
    </ModalShell>
  );
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: start;
  margin: 40px 0;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  padding: 2px 12px;

  &::placeholder {
    opacity: 0.2;
  }
`;
